import { ENV_VARS } from '@env';
import { IS_LOCAL_DEV, SKIP_AUTH } from '@utils/constants';
import { jsonStringify } from '@utils/json';
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@utils/localStorage';
import { win } from '@utils/win';

/* eslint-disable mastery/no-fetch */
// Using raw fetch for local dev server communication
const devPort = ENV_VARS.VITE_API_PORT || '5321';
const localHttpProtocol =
  IS_LOCAL_DEV && win.location.protocol === 'https:' ? 'https' : 'http';
const TOKEN_API_BASE = `${localHttpProtocol}://localhost:${devPort}/dev/tokens`;
const PRIMARY_BROWSER_KEY = 'mm-primary-auth-browser';

interface StoredTokens {
  token?: string;
  refreshToken?: string;
  idToken?: string;
  idTokenParsed?: anyOk;
  tokenParsed?: anyOk;
  refreshTokenParsed?: anyOk;
  timeSkew?: number;
}

/**
 * Check if token storage is enabled (only in local dev, not when skipping auth)
 */
const isTokenStorageEnabled = (): boolean => {
  return IS_LOCAL_DEV && !SKIP_AUTH;
};

/**
 * Save tokens to the server for reuse across browser instances
 */
export const saveTokensToStorage = async (
  tokens: StoredTokens,
): Promise<void> => {
  if (!isTokenStorageEnabled()) {
    return;
  }

  try {
    await fetch(TOKEN_API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: jsonStringify(tokens),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save tokens to server:', err);
  }
};

/**
 * Load tokens from the server
 */
export const loadTokensFromStorage = async (): Promise<StoredTokens | null> => {
  if (!isTokenStorageEnabled()) {
    return null;
  }

  try {
    const response = await fetch(TOKEN_API_BASE);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    // Check if tokens exist (not an empty object)
    if (data && data.token) {
      return data as StoredTokens;
    }
    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load tokens from server:', err);
    return null;
  }
};

/**
 * Clear stored tokens from the server
 */
export const clearStoredTokens = async (): Promise<void> => {
  if (!isTokenStorageEnabled()) {
    return;
  }

  // Clear primary browser marker
  removeLocalStorage(PRIMARY_BROWSER_KEY);

  try {
    await fetch(TOKEN_API_BASE, {
      method: 'DELETE',
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to clear tokens from server:', err);
  }
};

/**
 * Mark this browser as the primary auth browser (the one that performed login)
 */
export const markAsPrimaryBrowser = (): void => {
  if (!isTokenStorageEnabled()) {
    return;
  }
  setLocalStorage(PRIMARY_BROWSER_KEY, 'true');
};

/**
 * Check if this browser is the primary auth browser
 */
export const isPrimaryBrowser = (): boolean => {
  if (!isTokenStorageEnabled()) {
    return true; // Default to primary behavior when feature is disabled
  }
  return getLocalStorage(PRIMARY_BROWSER_KEY) === 'true';
};
/* eslint-enable mastery/no-fetch */
